import { useEffect, useState } from 'react';
import { AppHeader } from '../components/AppHeader';
import { LoadingView } from '../components/LoadingView';
import { Notice } from '../components/Notice';
import type { ParentStatus } from '../services/parentApi';
import { fetchParentStatus, logout, requestLoginCode, revokeConsent, submitConsent, verifyLoginCode } from '../services/parentApi';

interface GrownUpsScreenProps {
  onBack: () => void;
}

interface AdultGate { question: string; answer: number }

/** A quick adult gate: a small multiplication question kids this age will not solve casually. */
function makeAdultGate(): AdultGate {
  const a = 6 + Math.floor(Math.random() * 4);
  const b = 6 + Math.floor(Math.random() * 4);
  return { question: `What is ${a} × ${b}?`, answer: a * b };
}

function useAdultGate(): AdultGate | undefined {
  const [gate, setGate] = useState<AdultGate>();
  useEffect(() => { setGate(makeAdultGate()); }, []);
  return gate;
}

const CONSENT_TEXT = [
  'The Magic Eraser sends a copy of the chosen photo over the internet to an AI image service (Google Gemini) so it can remove things like a helping hand from the picture.',
  'Photos are sent only when your child taps the eraser button, are not stored on our server, and are not used to train AI models under the paid API terms.',
  'Everything else in the app stays on this device. No account is needed to make movies.',
  'You can turn this off at any time on this page. Turning it off hides the eraser right away.',
];

export function GrownUpsScreen({ onBack }: GrownUpsScreenProps) {
  const gate = useAdultGate();
  const [gateAnswer, setGateAnswer] = useState('');
  const [gatePassed, setGatePassed] = useState(false);
  const [gateError, setGateError] = useState('');
  const [status, setStatus] = useState<ParentStatus>();
  const [offline, setOffline] = useState(false);
  const [email, setEmail] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [form, setForm] = useState({ parentFirstName: '', parentLastName: '', birthYear: '', childFirstName: '', childLastName: '', agreed: false });

  useEffect(() => {
    fetchParentStatus().then(setStatus).catch(() => setOffline(true));
  }, []);

  function checkGate(event: React.FormEvent) {
    event.preventDefault();
    if (gate && Number(gateAnswer.trim()) === gate.answer) { setGatePassed(true); setGateError(''); }
    else setGateError('That is not right. This page is for grown-ups.');
  }

  async function sendCode(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setMessage('');
    try {
      await requestLoginCode(email.trim());
      setCodeSent(true);
      setCode('');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'The sign-in email could not be sent.');
    } finally {
      setBusy(false);
    }
  }

  async function checkCode(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setMessage('');
    try {
      await verifyLoginCode(email.trim(), code.trim());
      setStatus(await fetchParentStatus());
      setCodeSent(false);
      setCode('');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'That code did not work. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  async function saveConsent(event: React.FormEvent) {
    event.preventDefault();
    if (!status) return;
    setBusy(true);
    setMessage('');
    try {
      await submitConsent({
        parentFirstName: form.parentFirstName,
        parentLastName: form.parentLastName,
        birthYear: Number(form.birthYear),
        childFirstName: form.childFirstName,
        childLastName: form.childLastName,
        agreed: form.agreed,
        consentVersion: status.consentVersion,
      });
      setStatus(await fetchParentStatus());
      setMessage('The Magic Eraser is now on. Your child will see a ✨ button next to their pictures.');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'The consent could not be saved.');
    } finally {
      setBusy(false);
    }
  }

  async function turnOff() {
    setBusy(true);
    setMessage('');
    try {
      await revokeConsent();
      setStatus(await fetchParentStatus());
      setMessage('The Magic Eraser is now off.');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'That did not work. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  async function signOut() {
    await logout();
    setStatus(await fetchParentStatus().catch(() => undefined));
    setCodeSent(false);
    setCode('');
    setMessage('You are signed out.');
  }

  if (!gatePassed) {
    return (
      <div className="screen-shell">
        <AppHeader title="For Grown-Ups" onHome={onBack} />
        <main className="narrow-screen grownups-screen">
          <p className="muted">This page has settings for parents and carers.</p>
          <form className="new-movie-form" onSubmit={checkGate}>
            <label htmlFor="gate-answer">{gate?.question ?? '…'}</label>
            <input id="gate-answer" inputMode="numeric" autoComplete="off" value={gateAnswer} onChange={(e) => setGateAnswer(e.target.value)} />
            {gateError && <p className="form-error" role="alert">{gateError}</p>}
            <button className="button primary big-button" type="submit">Continue</button>
          </form>
        </main>
      </div>
    );
  }

  const content = () => {
    if (offline) return <Notice tone="warning" role="status">The grown-ups area needs the internet. Everything else in the app still works.</Notice>;
    if (!status) return <LoadingView />;
    if (!status.signedIn) {
      return codeSent ? (
        <form className="new-movie-form" onSubmit={(e) => { void checkCode(e); }}>
          <Notice tone="success" role="status">We emailed a 6-digit code to <strong>{email}</strong>. It works for 10 minutes.</Notice>
          <label htmlFor="login-code">Type the code here</label>
          <input id="login-code" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} required value={code} onChange={(e) => setCode(e.target.value)} />
          <button className="button primary big-button" type="submit" disabled={busy || code.trim().length !== 6}>{busy ? 'Checking…' : 'Sign in'}</button>
          <button className="button quiet" type="button" disabled={busy} onClick={(e) => { void sendCode(e); }}>Send a new code</button>
          <button className="button quiet" type="button" disabled={busy} onClick={() => { setCodeSent(false); setCode(''); setMessage(''); }}>Use a different email</button>
        </form>
      ) : (
        <form className="new-movie-form" onSubmit={(e) => { void sendCode(e); }}>
          <h2>Sign in with email</h2>
          <p className="muted">No passwords — we email you a one-time code to type in here.</p>
          <label htmlFor="parent-email">Your email</label>
          <input id="parent-email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          <button className="button primary big-button" type="submit" disabled={busy}>{busy ? 'Sending…' : 'Email me a sign-in code'}</button>
        </form>
      );
    }
    if (status.consent) {
      return (
        <section className="consent-status">
          <Notice tone="success" role="status">The Magic Eraser is <strong>on</strong> for {status.consent.childFirstName}.</Notice>
          <p className="muted">Turned on {new Date(status.consent.consentedAt).toLocaleDateString()} · consent version {status.consent.version} · signed in as {status.email}</p>
          <div className="dialog-actions stacked">
            <button className="button danger" type="button" disabled={busy} onClick={() => { void turnOff(); }}>Turn the Magic Eraser off</button>
            <button className="button quiet" type="button" onClick={() => { void signOut(); }}>Sign out</button>
          </div>
        </section>
      );
    }
    return (
      <form className="new-movie-form consent-form" onSubmit={(e) => { void saveConsent(e); }}>
        <h2>Turn on the Magic Eraser</h2>
        <div className="consent-text">
          {CONSENT_TEXT.map((line) => <p key={line}>{line}</p>)}
        </div>
        <label htmlFor="p-first">Your first name</label>
        <input id="p-first" required autoComplete="given-name" value={form.parentFirstName} onChange={(e) => setForm({ ...form, parentFirstName: e.target.value })} />
        <label htmlFor="p-last">Your last name</label>
        <input id="p-last" required autoComplete="family-name" value={form.parentLastName} onChange={(e) => setForm({ ...form, parentLastName: e.target.value })} />
        <label htmlFor="p-year">Your year of birth</label>
        <input id="p-year" required inputMode="numeric" pattern="[0-9]{4}" value={form.birthYear} onChange={(e) => setForm({ ...form, birthYear: e.target.value })} />
        <label htmlFor="c-first">Child&apos;s first name</label>
        <input id="c-first" required value={form.childFirstName} onChange={(e) => setForm({ ...form, childFirstName: e.target.value })} />
        <label htmlFor="c-last">Child&apos;s last name</label>
        <input id="c-last" required value={form.childLastName} onChange={(e) => setForm({ ...form, childLastName: e.target.value })} />
        <label className="consent-check">
          <input type="checkbox" checked={form.agreed} onChange={(e) => setForm({ ...form, agreed: e.target.checked })} />
          <span>I am this child&apos;s parent or guardian and I agree to the Magic Eraser terms above.</span>
        </label>
        <button className="button primary big-button" type="submit" disabled={busy || !form.agreed}>{busy ? 'Saving…' : 'Turn it on'}</button>
        <button className="button quiet" type="button" onClick={() => { void signOut(); }}>Sign out</button>
      </form>
    );
  };

  return (
    <div className="screen-shell">
      <AppHeader title="For Grown-Ups" onHome={onBack} />
      <main className="narrow-screen grownups-screen">
        {message && <Notice tone="info" role="status">{message}</Notice>}
        {content()}
      </main>
    </div>
  );
}
